import { latLngBounds, type LatLngBounds, type LatLngTuple, type PathOptions } from 'leaflet';
import type { MultiPolygon } from 'noddi-types';
import { noddiColors } from 'noddi-ui-common';

export const MODES = {
  NONE: 'none',
  H3_EDIT: 'h3Edit',
  POLYGON_ADD: 'polygonAdd',
  POLYGON_REMOVE: 'polygonRemove',
  BATCH_ADD: 'batchAdd',
  BATCH_REMOVE: 'batchRemove'
} as const;

export const INITIAL_CENTER: LatLngTuple = [59.911, 10.7579];
export const INITIAL_ZOOM = 13;
export const MIN_ZOOM_LEVEL_CLICK = 17;
export const H3_RESOLUTION = 11;

export const MAX_ZOOM = 18;
export const MIN_ZOOM = 3;

export const CLOSE_VERTEX_THRESHOLD_PX = 15;
export const MIN_POLYGON_AREA_SQ_M = 100;

export const POLYGON_STYLES: {
  unselected: PathOptions;
  selected: PathOptions;
  selectedForAction: PathOptions;
  remove: PathOptions;
  other: PathOptions;
  loading: PathOptions;
  drawPreviewAdd: PathOptions;
  drawPreviewRemove: PathOptions;
} = {
  unselected: {
    color: noddiColors.black,
    fillColor: noddiColors.darkPurple,
    weight: 1,
    opacity: 0.1,
    fillOpacity: 0
  },
  selected: {
    color: noddiColors.black,
    fillColor: noddiColors.success,
    weight: 1,
    opacity: 1.0,
    fillOpacity: 0.3
  },
  selectedForAction: {
    color: noddiColors.black,
    fillColor: noddiColors.warning,
    weight: 2,
    opacity: 1.0,
    fillOpacity: 0.5
  },
  remove: {
    color: noddiColors.black,
    fillColor: noddiColors.danger50,
    weight: 1,
    opacity: 1.0,
    fillOpacity: 0.3
  },
  other: {
    color: noddiColors.black,
    fillColor: noddiColors.danger,
    weight: 1,
    opacity: 1.0,
    fillOpacity: 0.3
  },
  loading: {
    color: noddiColors.black,
    weight: 1,
    opacity: 1.0,
    fillOpacity: 0.3
  },
  drawPreviewAdd: {
    color: noddiColors.success,
    fillColor: noddiColors.success,
    weight: 2,
    opacity: 0.8,
    fillOpacity: 0.15,
    dashArray: '6, 6'
  },
  drawPreviewRemove: {
    color: noddiColors.danger,
    fillColor: noddiColors.danger50,
    weight: 2,
    opacity: 0.8,
    fillOpacity: 0.15,
    dashArray: '6, 6'
  }
};

const MAPBOX_TOKEN = import.meta.env.VITE_APP_MAPBOX_API_KEY;

interface MapLayer {
  label: string;
  url: string;
  /** Transparent label tiles drawn on top of `url`, for basemaps that ship without place names. */
  referenceUrl?: string;
  tileSize: number;
  zoomOffset: number;
  /**
   * Highest zoom the server actually draws. Past this, Leaflet scales that tile.
   * Omit when the server has tiles for every zoom the map allows.
   */
  maxNativeZoom?: number;
}

export type MapLayerKey = 'streets' | 'satellite' | 'light';

export const MAP_LAYERS: Record<MapLayerKey, MapLayer> = {
  streets: {
    label: 'Streets',
    url: `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/512/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`,
    tileSize: 512,
    zoomOffset: -1
  },
  satellite: {
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    tileSize: 256,
    zoomOffset: 0
  },
  // Esri's light gray canvas replaces CARTO's light_all, which now watermarks every tile served
  // without an API key. Esri splits the style in two: a label-free base plus a reference overlay.
  // The service lists levels through 23, but from 17 up the tile is the "Map data not yet available"
  // placeholder. Scale level 16 instead of requesting those.
  light: {
    label: 'Light',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    referenceUrl:
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}',
    tileSize: 256,
    zoomOffset: 0,
    maxNativeZoom: 16
  }
};

/**
 * Compute Leaflet LatLngBounds from a MultiPolygon coordinate array.
 *
 * MultiPolygon is `[number, number][][][]` (polygons → rings → points).
 * `flat(2)` collapses those 3 levels into a flat `[number, number][]` list of
 * all coordinate points, which Leaflet's `latLngBounds` uses to find the
 * bounding rectangle. Returns null when the polygon array is empty.
 */
export const getMultiPolygonBounds = (polygons: MultiPolygon): LatLngBounds | null => {
  const points = polygons.flat(2);
  if (points.length === 0) {
    return null;
  }
  return latLngBounds(points);
};
